import { HgSupportedLanguage } from '@smg-real-estate/domus';
import {
  RouteLocationNormalized,
  RouteLocationRaw,
  RouteRecordRaw,
  Router,
  createMemoryHistory,
  createRouter,
  createWebHistory,
} from 'vue-router';

import { DEFAULT_LANGUAGE, setLocaleBeforeRouteChange } from '@/i18n-setup';

import { createAuth0Instance } from '@/helpers/Auth/authClient';
import { getCurrentPlatform, isPlatformIs24, isServer } from '@/helpers/Platform/Platform';
import { getLoginCallbackUrl } from '@/helpers/Route/routeHelper';
import { Pages } from '@/types';
import { Auth0Plugin } from '@auth0/auth0-vue';
import { setupRumViewTracking } from '@smg-real-estate/browser-rum/vue';
import { BASE_PATH } from './routes/constants';
import { getIV24IFrameValuationFunnelRoute, getIV24ResultRoute } from './routes/iv24Routes';
import getLandingPageRoute from './routes/landingPage';
import getLocalLandingPageRoute from './routes/localLandingPage';
import getValuationFunnelResultRoute from './routes/result';
import getValuationFunnelRoute from './routes/valuationFunel';

const iv24Routes = isPlatformIs24()
  ? [...getIV24IFrameValuationFunnelRoute(), ...getIV24ResultRoute()]
  : [];

export function getAllRoutes(): RouteRecordRaw[] {
  return [
    ...getValuationFunnelResultRoute,
    ...getValuationFunnelRoute,
    ...iv24Routes,
    ...getLocalLandingPageRoute(),
    ...getLandingPageRoute(),
    {
      path: BASE_PATH['de'] + '/rueckruf',
      name: Pages.IV24Callback,
      component: () => import('@/pages/LandingPage/LandingPageIv24.vue'),
      meta: {
        getLanguageSwitchLink(route: RouteLocationNormalized) {
          return {
            name: route.name,
            query: route.query,
            hash: route.hash,
          };
        },
      },
    },
    {
      path: getLoginCallbackUrl(false),
      name: 'LoginCallback',
      component: () => import('@/pages/Login/LoginCallback.vue'),
      meta: {
        getLanguageSwitchLink(route: RouteLocationNormalized, locale?: HgSupportedLanguage) {
          return {
            name: route.name,
            params: {
              locale: locale ?? DEFAULT_LANGUAGE,
            },
          };
        },
      },
    },
    {
      path: '/:pageNotFound*',
      name: 'pageNotFound',
      component: () => import('@smg-real-estate/domus/HgPageNotFound.vue'),
      props: () => ({
        pageLayoutCssClass: 'layout-container',
        theme: getCurrentPlatform(),
      }),
      meta: {
        getLanguageSwitchLink(route: RouteLocationNormalized) {
          return {
            path: route.path,
            query: route.query,
            hash: route.hash,
          };
        },
      },
    },
  ];
}

function defaultGetLanguageSwitchLink(route: RouteLocationNormalized): RouteLocationRaw {
  return {
    path: route.path,
    query: route.query,
    hash: route.hash,
  };
}

const getHistoryBase = (): string => import.meta.env.BASE_URL || '/';

export function createAppRouter(): Router {
  return createRouter({
    history: isServer()
      ? createMemoryHistory(getHistoryBase())
      : createWebHistory(getHistoryBase()),
    routes: getAllRoutes(),
    scrollBehavior(to, from, savedPosition) {
      if (savedPosition) {
        return savedPosition;
      }
      if (to.meta?.scrollToTop) {
        return { left: 0, top: 0 };
      }
      return false;
    },
  });
}

export const router = createAppRouter();

export function initGuards(router: Router) {
  router.beforeEach((to) => {
    if (typeof to.meta.getLanguageSwitchLink !== 'function') {
      to.meta.getLanguageSwitchLink = defaultGetLanguageSwitchLink;
    }
  });

  router.beforeEach(async (to, _, next) => {
    const locale =
      ((to?.params?.locale ?? to?.meta?.locale) as HgSupportedLanguage) || DEFAULT_LANGUAGE;
    await setLocaleBeforeRouteChange(locale);

    if (isServer()) {
      next();
      return;
    }

    const { isAuthenticated, loginWithRedirect, checkSession } =
      createAuth0Instance() as Auth0Plugin;

    try {
      await checkSession();
    } catch (error) {
      // ignore
    }

    if (to.meta?.requiresAuth && !isAuthenticated.value) {
      await loginWithRedirect({
        authorizationParams: {
          ui_locales: locale,
        },
        appState: {
          target: to.fullPath,
        },
      });
      next(false);
    }

    next();
  });

  setupRumViewTracking(router);
}
